import React, { FC, FormHTMLAttributes } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { Field, FormRenderProps } from 'react-final-form';

import { t } from 'utils';
import { Button, Heading } from 'components/ui';
import { AppStatus, ITooltipFields } from './constants';
import Subscribe from './components/Subscribe/Subscribe';
import Statistics from './components/Statistics/Statistics';
import BranchInfo from './components/BranchInfo/BranchInfo';
import LogoUploader from './components/LogoUploader/LogoUploader';
import IdsAndSecrets from './components/IdsAndSecrets/IdsAndSecrets';
import PackageUpload from './components/PackageUpload/PackageUpload';
import AppIcon from './components/AppIcon/AppIcon';
import { RichTextWrapper, SwitchInputWrapper, TextInputWrapper } from 'components/forms';
import {
  Error,
  SuccessMessage,
  TimifyLoader,
  TooltipConfirm,
  TypesInfo,
  Notification,
} from 'components';
import { logoSize } from 'constants/others';

import styles from './AppSettings.module.scss';
import { PreviewSizeSelector } from 'components/ImagePreview/ImagePreview';
import routesByName from '../../../constants/routes';

export interface ICompanyOrEnterprise {
  id: string;
  name: string;
  isEnterprise: boolean;
  enterprise?: Array<{ id: string; name: string }>;
}

export interface IRole {
  name: string;
  types: string[];
}

export enum iconTypes {
  default = 'DEFAULT',
  image = 'IMAGE',
  fontAwesome = 'FONTAWESOME',
}

export interface IAppSettingsForm
  extends Omit<FormHTMLAttributes<HTMLFormElement>, 'children'>,
    FormRenderProps<any, Partial<any>> {
  id: string;
  status: AppStatus;
  roles: IRole[];
  selectedPlan?: string;
  isFormDisabled: boolean;
  installmentsCount: string;
  companiesAndEnterprises: ICompanyOrEnterprise[];
  isAuthoriseAccountsEnabled: boolean;
  allowFullscreenView: boolean;
  tooltipData: ITooltipFields;
  iconType: iconTypes;
  iconTextColor: string;
  iconImageUrl: string;
  iconBackgroundColor: string;
  iconFontAwesomeName: string;
  abbreviatedName: string;
  handleEditPlanModalOpen: () => void;
  handleRemove: () => void;
  handleAuthoriseModalOpen: () => void;
  handleAddNewScopeModalOpen: () => void;
}

const AppSettingsForm: FC<IAppSettingsForm> = ({
  id,
  form,
  roles,
  dirty,
  status,
  pristine,
  className,
  submitting,
  submitError,
  handleSubmit,
  tooltipData,
  selectedPlan,
  submitErrors,
  handleRemove,
  isFormDisabled,
  submitSucceeded,
  installmentsCount,
  companiesAndEnterprises,
  modifiedSinceLastSubmit,
  handleEditPlanModalOpen,
  handleAuthoriseModalOpen,
  dirtyFieldsSinceLastSubmit,
  isAuthoriseAccountsEnabled,
  handleAddNewScopeModalOpen,
  initialValues,
  values: {
    iconType,
    iconImageUrl,
    iconTextColor,
    abbreviatedName,
    allowFullscreenView,
    iconBackgroundColor,
    iconFontAwesomeName,
    isCustomProdUrlEnabled,
  },
}) => {
  const history = useHistory();
  if (status === AppStatus.BLOCKED) {
    history.push(routesByName.apps.index);
  }
  const rootClassName = classNames(styles.root, 'row d-flex', className);

  return (
    <form onSubmit={handleSubmit}>
      {submitting && <TimifyLoader overlay />}
      <div className={rootClassName}>
        <div className={classNames(styles.main, 'col')}>
          <section className={styles.section}>
            <Heading icon='font'>{t('application.details')}</Heading>
            <LogoUploader
              className='mb-2'
              previewSize={PreviewSizeSelector.large}
              disabled={isFormDisabled}
              allowedHeight={logoSize.height}
              allowedWidth={logoSize.width}
              imageFieldName='imageFile'
              acceptFileTypes='image/*'
              label='app.preview.settings.appIcon.label'
              note='app.preview.settings.appIcon.hintText'
              placeholder='app.preview.settings.appIcon.placeholder'
              isFormSubmitSuccess={submitSucceeded && !modifiedSinceLastSubmit}
            />
            <AppIcon
              disabled={isFormDisabled}
              className='mb-2'
              iconType={iconType}
              iconImageUrl={iconImageUrl}
              iconTextColor={iconTextColor}
              abbreviatedName={abbreviatedName}
              iconBackgroundColor={iconBackgroundColor}
              iconFontAwesomeName={iconFontAwesomeName}
              errors={submitErrors}
              initialValues={initialValues}
              form={form}
            />
            <PackageUpload disabled={isFormDisabled} className='mb-2' name='backendUrl' />
            <Field
              label='app.description.optional'
              name='description'
              className='mb-2'
              component={RichTextWrapper}
              disabled={isFormDisabled}
            />
            <Field
              className={`mb-${isCustomProdUrlEnabled ? '1' : '4'}`}
              name='isCustomProdUrlEnabled'
              label='app.settings.prodUrl.label'
              type='checkbox'
              component={SwitchInputWrapper}
              disabled={isFormDisabled}
              initialValue={isCustomProdUrlEnabled}
            />
            {isCustomProdUrlEnabled && (
              <>
                <Field
                  className='mb-1'
                  label='app.settings.hookUrl.label'
                  placeholder='app.settings.hookUrl.placeholder'
                  name='hookUrl'
                  disabled={isFormDisabled}
                  error={!dirtyFieldsSinceLastSubmit?.hookUrl ? submitErrors?.hookUrl : null}
                  // validate={(value = '', allValues: any, meta: any) => {
                  //   const { pristine } = meta;
                  //   const { isCustomProdUrlEnabled } = allValues;
                  //   if (isCustomProdUrlEnabled && !pristine && !value.length)
                  //     return t('errors.hookUrl.mandatory');
                  // }}
                  component={TextInputWrapper}
                />
                <Field
                  className='mb-4'
                  label='app.settings.hookSecret.label'
                  placeholder='app.settings.hookSecret.placeholder'
                  name='hookSecret'
                  error={!dirtyFieldsSinceLastSubmit?.hookSecret ? submitErrors?.hookSecret : null}
                  disabled={isFormDisabled}
                  // validate={(value = '', allValues: any, meta: any) => {
                  //   const { pristine } = meta;
                  //   const { isCustomProdUrlEnabled } = allValues;
                  //   if (isCustomProdUrlEnabled && !pristine && !value.length)
                  //     return t('errors.hookSecret.mandatory');
                  // }}
                  required
                  component={TextInputWrapper}
                />
              </>
            )}
            <Heading icon='list-ul' noTopPadding>
              {t('app.fullscreen.section')}
            </Heading>
            <Notification
              className='mb-2'
              title='app.fullscreen.warning.title'
              hasCloseButton
              hasDontShowButton='settings-fullscreen'
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: t('app.fullscreen.warning.text'),
                }}
              />
            </Notification>
            <Field
              name='allowFullscreenView'
              label='app.settings.fullscreen.label'
              type='checkbox'
              component={SwitchInputWrapper}
              disabled={isFormDisabled}
              initialValue={allowFullscreenView}
            />
            <Button
              inline
              disabled={submitSucceeded && !modifiedSinceLastSubmit}
              className={classNames(
                { 'd-none': !dirty || pristine || isFormDisabled },
                `mt-${isCustomProdUrlEnabled ? '3' : '2'}`
              )}
            >
              {t('save.changes')}
            </Button>
            <SuccessMessage
              className='mt-2'
              isSuccess={submitSucceeded && !modifiedSinceLastSubmit}
            >
              {t('global.changesSaved')}
            </SuccessMessage>
            {submitError && (
              <Error
                apiError={submitError}
                defaultError='errors.somethingWentWrong'
                isError={submitError && !modifiedSinceLastSubmit}
                className='mt-2'
              />
            )}
          </section>
          <section className={styles.section}>
            <Heading icon='boxes' noTopPadding>
              {t('application.plans')}
            </Heading>
            <TypesInfo className={styles.types} name={selectedPlan ?? ''} types={[]} />
            <Button
              className='mt-4'
              onClick={handleEditPlanModalOpen}
              disabled={isFormDisabled}
              variant='ghost'
              icon='plus'
              color='dark'
              type='button'
              medium
            >
              {t('add.application.plan')}
            </Button>
          </section>
          <section className={styles.section}>
            <Heading icon='list-ul' noTopPadding>
              {t('application.scope')}
            </Heading>
            {roles.map((role: any) => (
              <TypesInfo
                className={styles.types}
                name={role.name}
                types={role.types}
                key={role.name}
              />
            ))}
            {!isFormDisabled && (
              <Button
                className='mt-4'
                onClick={handleAddNewScopeModalOpen}
                variant='ghost'
                color='dark'
                icon='plus'
                type='button'
                medium
              >
                {t('add.application.scope')}
              </Button>
            )}
          </section>
          <section className={styles.section}>
            <Heading icon='store-alt' noTopPadding>
              {t('authorize.accounts')}
            </Heading>
            <p className={styles.text}>{t('app.preview.settings.authorizeAccounts.hintText')}</p>
            {companiesAndEnterprises.map(
              ({ id: companyId, name, enterprise, isEnterprise }: any) => (
                <BranchInfo
                  className={styles.branchInfo}
                  title={name}
                  appId={id}
                  companyId={companyId}
                  isEnterprise={isEnterprise}
                  enterprise={enterprise}
                  status='installed'
                  key={companyId}
                />
              )
            )}
            <Button
              onClick={handleAuthoriseModalOpen}
              variant='ghost'
              color='dark'
              icon='plus'
              type='button'
              disabled={!isAuthoriseAccountsEnabled}
              medium
            >
              {t('authorize.another.account')}
            </Button>
          </section>
        </div>
        <div className={classNames(styles.aside, 'col')}>
          <Subscribe status={status} />
          <IdsAndSecrets />
          {status === AppStatus.PUBLISHED && (
            <Statistics installations={installmentsCount} activeUsers='0' />
          )}
          {tooltipData && (
            <section className={styles.removeSection}>
              <TooltipConfirm
                title={tooltipData.title}
                confirmText={tooltipData.confirmText}
                text={tooltipData.text}
                onConfirm={handleRemove}
              >
                <Button type='button' color='dark' variant='ghost'>
                  {t(tooltipData.buttonText)}
                </Button>
              </TooltipConfirm>
            </section>
          )}
        </div>
      </div>
    </form>
  );
};

export default AppSettingsForm;
